// Data displays a child reads a value from (owner's decision 5 Oct 2026:
// give the graph skills real pictures instead of describing a graph in
// words). Unlike the older 'bars' and 'line_graph' diagrams, these never
// print the values on the picture: the child reads them off the scale, the
// key or the table, which is the skill being practised.
//
// Pure parsing only, no components (see practice-diagram-data.ts).

import {
  readAngle,
  readAnglesAtPoint,
  readTransversal,
  type AngleSpec,
  type AnglesAtPointSpec,
  type TransversalSpec,
} from './practice-angle-data';
import { readAreaModel, type AreaModelSpec } from './practice-area-model-data';
import { readBarModel, type BarModelSpec } from './practice-bar-model-data';
import { readCircleParts, type CirclePartsSpec } from './practice-circle-data';
import { readClock, type ClockSpec } from './practice-clock-data';
import {
  readArray,
  readBaseTen,
  readCounters,
  type CountSpec,
} from './practice-count-data';
import {
  readJug,
  readRuler,
  readScale,
  type JugSpec,
  type RulerSpec,
  type ScaleSpec,
} from './practice-measure-data';
import {
  readEverydayObject,
  type EverydayObjectSpec,
} from './practice-everyday-object-data';
import {
  readHundredChart,
  type HundredChartSpec,
} from './practice-hundred-chart-data';
import {
  readBalance,
  readCups,
  readUnitLine,
  type InformalMeasureSpec,
} from './practice-informal-measure-data';
import { readLineUp, type LineUpSpec } from './practice-line-up-data';
import { readMoney, type MoneySpec } from './practice-money-data';
import {
  readPicturePattern,
  type PicturePatternSpec,
} from './practice-picture-pattern-data';
import { readTenFrame, type TenFrameSpec } from './practice-ten-frame-data';
import { readTurnPicture, type TurnPictureSpec } from './practice-turn-data';
import { readShadeGrid, type ShadeGridSpec } from './practice-shade-grid-data';
import {
  readFractionModel,
  type FractionModelSpec,
} from './practice-fraction-model-data';
import {
  readDigitalClock,
  readThermometer,
  type DigitalClockSpec,
  type ThermometerSpec,
} from './practice-instrument-data';
import {
  readNumberLine,
  type NumberLineSpec,
} from './practice-number-line-data';
import {
  readCompositeShape,
  readShape2d,
  type CompositeShapeSpec,
  type Shape2dSpec,
} from './practice-shape-data';
import { readCubeStack, type CubeStackSpec } from './practice-cube-stack-data';
import {
  readCurvedShape,
  type CurvedShapeSpec,
} from './practice-curved-shape-data';
import { readShapePair, type ShapePairSpec } from './practice-shape-pair-data';
import {
  readQuadDiagonals,
  type QuadDiagonalsSpec,
} from './practice-quad-diagonals-data';
import {
  readTreeDiagram,
  type TreeDiagramSpec,
} from './practice-tree-diagram-data';
import { readVenn, type VennSpec } from './practice-venn-data';
import { readAxes3d, type Axes3dSpec } from './practice-axes-3d-data';
import {
  readNet,
  readNetGrid,
  readSolid3d,
  type NetGridSpec,
  type NetSpec,
  type Solid3dSpec,
} from './practice-solid-data';

export type GraphColumn = { label: string; values: number[] };

export type DotStack = { value: number; count: number };

export type GraphDiagramSpec =
  | {
      type: 'column_graph' | 'line_chart';
      title: string | null;
      xLabel: string;
      yLabel: string;
      // The gap between marks on the vertical scale, and where it ends.
      step: number;
      max: number;
      // One name per set of columns or per line; null for a single set.
      series: string[] | null;
      columns: GraphColumn[];
    }
  | {
      type: 'picture_graph';
      title: string | null;
      // What one whole picture stands for, e.g. "2 books".
      key: string;
      rows: { label: string; count: number }[];
    }
  | {
      type: 'tally';
      title: string | null;
      rows: { label: string; count: number }[];
    }
  | {
      type: 'dot_plot';
      label: string;
      min: number;
      max: number;
      dots: DotStack[];
      // A name for the dots above, e.g. "Class A"; null when not given.
      group: string | null;
      // A second group drawn under the first on the same number line, so
      // two groups can be compared (Year 7, 2 Nov 2026). Both groups are
      // named. null for a single plot, which is drawn as before.
      second: { group: string; dots: DotStack[] } | null;
    }
  | {
      type: 'pie_chart';
      title: string | null;
      // The circle is marked into this many equal parts.
      parts: number;
      sectors: { label: string; parts: number }[];
    }
  | {
      type: 'table';
      title: string | null;
      headers: string[];
      rows: string[][];
      note: string | null;
    }
  | {
      // A number grid with labelled points. The coordinates are never
      // printed beside a point: the child reads them off the two axes.
      type: 'coordinate_grid';
      title: string | null;
      xMin: number;
      xMax: number;
      yMin: number;
      yMax: number;
      points: GridPoint[];
      // Each shape is a list of point labels joined in order: two labels
      // make a line, three or more a closed shape.
      shapes: string[][];
      // A dashed mirror line: 'vertical' is the line x = at.
      mirror: { direction: 'vertical' | 'horizontal'; at: number } | null;
      // Straight lines drawn right across the grid (Year 8, 2 Nov 2026).
      lines: GridLine[];
    }
  | {
      // A map with lettered columns and numbered rows; things sit in cells.
      type: 'map_grid';
      title: string | null;
      columns: number;
      rows: number;
      rowOneAt: 'bottom' | 'top';
      items: { label: string; column: number; row: number }[];
    }
  // A number line with ticks, numbers and lettered points
  // (practice-number-line-data.ts).
  | NumberLineSpec
  // A bar, circle or rectangle cut into equal parts, some shaded
  // (practice-fraction-model-data.ts).
  | FractionModelSpec
  // An empty grid of equal squares, for the student to shade
  // (practice-shade-grid.tsx).
  | ShadeGridSpec
  // Two lines meeting at an angle, or several angles round a point, on a
  // line, where lines cross or in a shape (practice-angle-data.ts).
  | AngleSpec
  | AnglesAtPointSpec
  // Two parallel lines crossed by a third (Year 7).
  | TransversalSpec
  // A named flat shape with labels and marks, or an outline whose sides all
  // meet at right angles (practice-shape-data.ts).
  | Shape2dSpec
  | CompositeShapeSpec
  // A solid drawn on a slant, or the flat pattern of faces that folds into
  // one (practice-solid-data.ts).
  | Solid3dSpec
  | NetSpec
  // A net laid out on a grid of squares, folding or not (Year 7).
  | NetGridSpec
  // A circle with its centre and parts (Year 7, practice-circle-data.ts).
  | CirclePartsSpec
  // A stack of cubes in 3D or as flat views (practice-cube-stack-data.ts).
  | CubeStackSpec
  // Half and quarter circles, and shapes made with them (Year 8).
  | CurvedShapeSpec
  // Two shapes side by side with their marks (Year 8).
  | ShapePairSpec
  // A shape with 4 straight sides and its diagonals (Year 8).
  | QuadDiagonalsSpec
  // A tree diagram, a Venn diagram, and a box on 3D axes (Year 8).
  | TreeDiagramSpec
  | VennSpec
  | Axes3dSpec
  // A thermometer read off its scale, or a clock that shows its time in
  // digits (practice-instrument-data.ts).
  | ThermometerSpec
  | DigitalClockSpec
  // A rectangle split into rows and columns to show a multiplication
  // (practice-area-model-data.ts).
  | AreaModelSpec
  // The Year 3 pictures (21 Oct 2026): a clock with hands
  // (practice-clock-data.ts); base-ten blocks, groups of things and arrays
  // to count (practice-count-data.ts); coins and notes
  // (practice-money-data.ts); a ruler, a measuring jug and a kitchen scale
  // read off their marks (practice-measure-data.ts).
  | ClockSpec
  | CountSpec
  | MoneySpec
  | RulerSpec
  | JugSpec
  | ScaleSpec
  // The Year 2 pictures (22 Oct 2026): a bar model (practice-bar-model-data.ts);
  // a repeating or growing pattern (practice-picture-pattern-data.ts); cups
  // that fill a container, units laid along an object and a two-pan balance
  // (practice-informal-measure-data.ts); a thing before and after a turn
  // (practice-turn-data.ts); one or two ten frames
  // (practice-ten-frame-data.ts); a hundred chart (practice-hundred-chart-data.ts).
  | BarModelSpec
  | PicturePatternSpec
  | InformalMeasureSpec
  | TurnPictureSpec
  | TenFrameSpec
  | HundredChartSpec
  // The Year 1 pictures (24 Oct 2026): a line of children or animals with a
  // "front" arrow (practice-line-up-data.ts); a thing from home or school to
  // sit beside the solids (practice-everyday-object-data.ts).
  | LineUpSpec
  | EverydayObjectSpec;

export type GridPoint = { label: string; x: number; y: number };

// A straight line on a coordinate grid (Year 8, wording approved 2 Nov 2026),
// given by two whole-number points it passes through. It is drawn across the
// whole grid, so the two points are not marked unless they are also in
// `points`. The label is a capital letter ("A") or the line's rule
// ("y = 2x + 1"), printed beside one end; a picture option may leave it out.
// The question writer picks two points that do not give the answer away,
// because they are read out to a screen reader.
export type GridLine = {
  label: string | null;
  through: [{ x: number; y: number }, { x: number; y: number }];
};

export type CoordinateGridSpec = Extract<
  GraphDiagramSpec,
  { type: 'coordinate_grid' }
>;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function readTitle(value: unknown): string | null {
  return isNonEmptyString(value) ? value : null;
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function readLabelCounts(
  value: unknown,
  allowHalves: boolean,
  highest: number,
): { label: string; count: number }[] | null {
  if (!Array.isArray(value) || value.length < 2 || value.length > 8) {
    return null;
  }
  const rows: { label: string; count: number }[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) return null;
    const record = entry as Record<string, unknown>;
    if (!isNonEmptyString(record.label) || !isCount(record.count)) return null;
    const scaled = allowHalves ? record.count * 2 : record.count;
    if (!Number.isInteger(scaled) || record.count > highest) return null;
    rows.push({ label: record.label, count: record.count });
  }
  return rows;
}

function readScaledGraph(
  type: 'column_graph' | 'line_chart',
  record: Record<string, unknown>,
): GraphDiagramSpec | null {
  if (
    !isNonEmptyString(record.xLabel) ||
    !isNonEmptyString(record.yLabel) ||
    typeof record.step !== 'number' ||
    record.step <= 0 ||
    !Array.isArray(record.columns) ||
    record.columns.length < 2 ||
    record.columns.length > 12
  ) {
    return null;
  }
  let series: string[] | null = null;
  if (record.series !== undefined && record.series !== null) {
    if (
      !Array.isArray(record.series) ||
      record.series.length !== 2 ||
      !record.series.every(isNonEmptyString)
    ) {
      return null;
    }
    series = record.series;
  }
  const perColumn = series ? series.length : 1;
  const columns: GraphColumn[] = [];
  let highest = 0;
  for (const entry of record.columns as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const column = entry as Record<string, unknown>;
    const values: unknown[] = Array.isArray(column.values)
      ? (column.values as unknown[])
      : [column.value];
    if (
      !isNonEmptyString(column.label) ||
      values.length !== perColumn ||
      !values.every(isCount)
    ) {
      return null;
    }
    highest = Math.max(highest, ...values);
    columns.push({ label: column.label, values });
  }
  const top = Math.ceil(highest / record.step) * record.step;
  const max =
    typeof record.max === 'number' && record.max >= top
      ? record.max
      : Math.max(top, record.step);
  // A scale with more than 12 marks cannot be read on a phone.
  if (max / record.step > 12) return null;
  return {
    type,
    title: readTitle(record.title),
    xLabel: record.xLabel,
    yLabel: record.yLabel,
    step: record.step,
    max,
    series,
    columns,
  };
}

// A group name sits above its plot, so it must be short enough to fit.
function readGroupName(value: unknown): string | null {
  return isNonEmptyString(value) && value.trim().length <= 24
    ? value.trim()
    : null;
}

function readDotPlot(record: Record<string, unknown>): GraphDiagramSpec | null {
  const { min, max } = record;
  if (
    !isNonEmptyString(record.label) ||
    typeof min !== 'number' ||
    typeof max !== 'number' ||
    !Number.isInteger(min) ||
    !Number.isInteger(max) ||
    max <= min ||
    max - min > 14
  ) {
    return null;
  }
  const dots = readDotStacks(record.dots, min, max);
  if (dots === null) return null;
  const group = readGroupName(record.group);
  if (record.group !== undefined && record.group !== null && group === null) {
    return null;
  }
  let second: { group: string; dots: DotStack[] } | null = null;
  if (record.second !== undefined && record.second !== null) {
    if (typeof record.second !== 'object') return null;
    const other = record.second as Record<string, unknown>;
    const otherGroup = readGroupName(other.group);
    const otherDots = readDotStacks(other.dots, min, max);
    // Two groups must both be named, and named differently, or the child
    // cannot tell which plot is which.
    if (
      group === null ||
      otherGroup === null ||
      otherDots === null ||
      otherGroup.toLowerCase() === group.toLowerCase()
    ) {
      return null;
    }
    second = { group: otherGroup, dots: otherDots };
  }
  return {
    type: 'dot_plot',
    label: record.label,
    min,
    max,
    dots,
    group,
    second,
  };
}

// The dots of one group: whole-number values on the line, at most 10 in a
// stack. null when any of them cannot be drawn.
function readDotStacks(
  value: unknown,
  min: number,
  max: number,
): DotStack[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const dots: DotStack[] = [];
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const dot = entry as Record<string, unknown>;
    if (
      typeof dot.value !== 'number' ||
      !Number.isInteger(dot.value) ||
      dot.value < min ||
      dot.value > max ||
      !isCount(dot.count) ||
      !Number.isInteger(dot.count) ||
      dot.count > 10
    ) {
      return null;
    }
    dots.push({ value: dot.value, count: dot.count });
  }
  return dots;
}

function readPieChart(
  record: Record<string, unknown>,
): GraphDiagramSpec | null {
  const { parts } = record;
  if (
    typeof parts !== 'number' ||
    !Number.isInteger(parts) ||
    parts < 2 ||
    parts > 12 ||
    !Array.isArray(record.sectors) ||
    record.sectors.length < 2 ||
    record.sectors.length > 5
  ) {
    return null;
  }
  const sectors: { label: string; parts: number }[] = [];
  let total = 0;
  for (const entry of record.sectors as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const sector = entry as Record<string, unknown>;
    if (
      !isNonEmptyString(sector.label) ||
      typeof sector.parts !== 'number' ||
      !Number.isInteger(sector.parts) ||
      sector.parts < 1
    ) {
      return null;
    }
    total += sector.parts;
    sectors.push({ label: sector.label, parts: sector.parts });
  }
  // The sectors must fill the circle exactly.
  if (total !== parts) return null;
  return { type: 'pie_chart', title: readTitle(record.title), parts, sectors };
}

function readTable(record: Record<string, unknown>): GraphDiagramSpec | null {
  const isText = (cell: unknown): cell is string => typeof cell === 'string';
  if (
    !Array.isArray(record.headers) ||
    record.headers.length < 2 ||
    // Seven, so a calendar week (Sun to Sat) fits in one row (21 Oct 2026).
    record.headers.length > 7 ||
    !record.headers.every(isText) ||
    !Array.isArray(record.rows) ||
    record.rows.length < 1 ||
    record.rows.length > 10
  ) {
    return null;
  }
  const width = record.headers.length;
  const rows: string[][] = [];
  for (const row of record.rows as unknown[]) {
    if (!Array.isArray(row) || row.length !== width || !row.every(isText)) {
      return null;
    }
    rows.push(row);
  }
  return {
    type: 'table',
    title: readTitle(record.title),
    headers: record.headers,
    rows,
    note: readTitle(record.note),
  };
}

function isWhole(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value);
}

function readCoordinateGrid(
  record: Record<string, unknown>,
): GraphDiagramSpec | null {
  const xMin = record.xMin === undefined ? 0 : record.xMin;
  const yMin = record.yMin === undefined ? 0 : record.yMin;
  const { xMax, yMax } = record;
  if (
    !isWhole(xMin) ||
    !isWhole(yMin) ||
    !isWhole(xMax) ||
    !isWhole(yMax) ||
    xMin > 0 ||
    yMin > 0 ||
    xMax < 1 ||
    yMax < 1 ||
    // More than 14 squares across cannot be read on a phone.
    xMax - xMin > 14 ||
    yMax - yMin > 14 ||
    !Array.isArray(record.points) ||
    record.points.length > 10
  ) {
    return null;
  }
  const points: GridPoint[] = [];
  const labels = new Set<string>();
  for (const entry of record.points as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const point = entry as Record<string, unknown>;
    if (
      !isNonEmptyString(point.label) ||
      point.label.length > 8 ||
      labels.has(point.label) ||
      !isWhole(point.x) ||
      !isWhole(point.y) ||
      point.x < xMin ||
      point.x > xMax ||
      point.y < yMin ||
      point.y > yMax
    ) {
      return null;
    }
    labels.add(point.label);
    points.push({ label: point.label, x: point.x, y: point.y });
  }
  const shapes: string[][] = [];
  if (record.shapes !== undefined && record.shapes !== null) {
    if (!Array.isArray(record.shapes) || record.shapes.length > 4) return null;
    for (const shape of record.shapes as unknown[]) {
      if (
        !Array.isArray(shape) ||
        shape.length < 2 ||
        !shape.every(
          (label): label is string =>
            typeof label === 'string' && labels.has(label),
        )
      ) {
        return null;
      }
      shapes.push(shape);
    }
  }
  let mirror: { direction: 'vertical' | 'horizontal'; at: number } | null =
    null;
  if (record.mirror !== undefined && record.mirror !== null) {
    if (typeof record.mirror !== 'object') return null;
    const line = record.mirror as Record<string, unknown>;
    if (
      (line.direction !== 'vertical' && line.direction !== 'horizontal') ||
      !isWhole(line.at)
    ) {
      return null;
    }
    const inside =
      line.direction === 'vertical'
        ? line.at >= xMin && line.at <= xMax
        : line.at >= yMin && line.at <= yMax;
    if (!inside) return null;
    mirror = { direction: line.direction, at: line.at };
  }
  const lines = readGridLines(record.lines, xMin, xMax, yMin, yMax);
  if (lines === null) return null;
  return {
    type: 'coordinate_grid',
    title: readTitle(record.title),
    xMin,
    xMax,
    yMin,
    yMax,
    points,
    shapes,
    mirror,
    lines,
  };
}

// A line's label: one capital letter, or a short rule such as "y = 2x + 1",
// "y = 9 − x" or "x = −3". Anything else is refused.
const LINE_LETTER = /^[A-Z]$/;
const LINE_RULE = /^[xy] = [0-9xy.+\-−×÷/() ]+$/;

// The `lines` of a coordinate grid: at most 4, each through two different
// whole-number points inside the grid, no two the same line, no label used
// twice. Missing means no lines; anything wrong spoils the whole picture.
function readGridLines(
  value: unknown,
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
): GridLine[] | null {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.length > 4) return null;
  const lines: GridLine[] = [];
  const labels = new Set<string>();
  for (const entry of value as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const line = entry as Record<string, unknown>;
    let label: string | null = null;
    if (line.label !== undefined && line.label !== null) {
      if (typeof line.label !== 'string') return null;
      const text = line.label.trim();
      if (
        text.length > 16 ||
        (!LINE_LETTER.test(text) && !LINE_RULE.test(text)) ||
        labels.has(text)
      ) {
        return null;
      }
      labels.add(text);
      label = text;
    }
    if (!Array.isArray(line.through) || line.through.length !== 2) {
      return null;
    }
    const ends: { x: number; y: number }[] = [];
    for (const point of line.through as unknown[]) {
      if (!Array.isArray(point) || point.length !== 2) return null;
      const [x, y] = point as unknown[];
      if (
        !isWhole(x) ||
        !isWhole(y) ||
        x < xMin ||
        x > xMax ||
        y < yMin ||
        y > yMax
      ) {
        return null;
      }
      ends.push({ x, y });
    }
    const [first, second] = ends;
    if (!first || !second) return null;
    if (first.x === second.x && first.y === second.y) return null;
    // The same line drawn twice would look like one line with two labels.
    if (lines.some((other) => sameLine(other, first, second))) return null;
    lines.push({ label, through: [first, second] });
  }
  return lines;
}

// Whether both points lie on the other line (cross products are zero).
function sameLine(
  line: GridLine,
  a: { x: number; y: number },
  b: { x: number; y: number },
): boolean {
  const [p, q] = line.through;
  const side = (point: { x: number; y: number }) =>
    (q.x - p.x) * (point.y - p.y) - (q.y - p.y) * (point.x - p.x);
  return side(a) === 0 && side(b) === 0;
}

// The part of a line that lies on the grid: where it enters and leaves the
// grid's edges, in grid units. Pure, for the drawing and its tests.
export function clipGridLine(
  grid: Pick<CoordinateGridSpec, 'xMin' | 'xMax' | 'yMin' | 'yMax'>,
  line: GridLine,
): [{ x: number; y: number }, { x: number; y: number }] {
  const [p, q] = line.through;
  const dx = q.x - p.x;
  const dy = q.y - p.y;
  // Liang-Barsky: narrow the range of t for p + t (q - p) edge by edge.
  let low = -Infinity;
  let high = Infinity;
  const narrow = (step: number, from: number, min: number, max: number) => {
    if (step === 0) return;
    const a = (min - from) / step;
    const b = (max - from) / step;
    low = Math.max(low, Math.min(a, b));
    high = Math.min(high, Math.max(a, b));
  };
  narrow(dx, p.x, grid.xMin, grid.xMax);
  narrow(dy, p.y, grid.yMin, grid.yMax);
  return [
    { x: p.x + low * dx, y: p.y + low * dy },
    { x: p.x + high * dx, y: p.y + high * dy },
  ];
}

// "Line A", "The line labelled y = 2x + 1", or, with no label, "A".
function lineSentence(line: GridLine): string {
  const [p, q] = line.through;
  const at = (point: { x: number; y: number }) =>
    `${String(point.x)}, ${String(point.y)}`;
  const kind =
    p.x === q.x
      ? `an upright line through ${at(p)}`
      : p.y === q.y
        ? `a flat line through ${at(p)}`
        : `a straight line through ${at(p)} and ${at(q)}`;
  if (line.label === null) {
    return `${kind.charAt(0).toUpperCase()}${kind.slice(1)}.`;
  }
  return LINE_LETTER.test(line.label)
    ? `Line ${line.label} is ${kind}.`
    : `The line labelled ${line.label} is ${kind}.`;
}

// What a screen reader says for a coordinate grid: the grid and its points
// as before, then one sentence per line. Never the line's rule unless it is
// printed on the picture, and never where it crosses an axis.
export function describeCoordinateGrid(grid: CoordinateGridSpec): string {
  const parts = [
    `A coordinate grid from ${String(grid.xMin)} to ${String(grid.xMax)} across and ${String(grid.yMin)} to ${String(grid.yMax)} up.`,
  ];
  if (grid.points.length > 0 || grid.lines.length === 0) {
    parts.push(
      `${grid.points
        .map(
          (point) => `${point.label} at ${String(point.x)}, ${String(point.y)}`,
        )
        .join('; ')}.`,
    );
  }
  for (const line of grid.lines) parts.push(lineSentence(line));
  return parts.join(' ');
}

// "C4" is column 3, row 4. Returns null for anything else.
function readCell(
  value: unknown,
  columns: number,
  rows: number,
): { column: number; row: number } | null {
  if (typeof value !== 'string') return null;
  const match = /^([A-Z])([0-9]{1,2})$/.exec(value.trim().toUpperCase());
  if (!match) return null;
  const column = (match[1] ?? '').charCodeAt(0) - 64;
  const row = Number(match[2]);
  if (column < 1 || column > columns || row < 1 || row > rows) return null;
  return { column, row };
}

function readMapGrid(record: Record<string, unknown>): GraphDiagramSpec | null {
  const { columns, rows } = record;
  if (
    !isWhole(columns) ||
    !isWhole(rows) ||
    columns < 2 ||
    columns > 10 ||
    rows < 2 ||
    rows > 10 ||
    !Array.isArray(record.items) ||
    record.items.length < 1 ||
    record.items.length > 8
  ) {
    return null;
  }
  const rowOneAt = record.rowOneAt === 'top' ? 'top' : 'bottom';
  const items: { label: string; column: number; row: number }[] = [];
  const taken = new Set<string>();
  for (const entry of record.items as unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const item = entry as Record<string, unknown>;
    const cell = readCell(item.cell, columns, rows);
    if (
      !isNonEmptyString(item.label) ||
      item.label.length > 9 ||
      cell === null ||
      // One thing per square, so a square names one thing.
      taken.has(`${String(cell.column)}-${String(cell.row)}`)
    ) {
      return null;
    }
    taken.add(`${String(cell.column)}-${String(cell.row)}`);
    items.push({ label: item.label, column: cell.column, row: cell.row });
  }
  return {
    type: 'map_grid',
    title: readTitle(record.title),
    columns,
    rows,
    rowOneAt,
    items,
  };
}

// null when the diagram is not one of these, or is not drawable as given
// (the caller then shows the question without a picture rather than a
// broken one).
export function readGraphDiagram(
  record: Record<string, unknown>,
): GraphDiagramSpec | null {
  if (record.type === 'column_graph' || record.type === 'line_chart') {
    return readScaledGraph(record.type, record);
  }
  if (record.type === 'picture_graph') {
    const rows = readLabelCounts(record.rows, true, 12);
    if (rows === null || !isNonEmptyString(record.key)) return null;
    return {
      type: 'picture_graph',
      title: readTitle(record.title),
      key: record.key,
      rows,
    };
  }
  if (record.type === 'tally') {
    const rows = readLabelCounts(record.rows, false, 30);
    if (rows === null) return null;
    return { type: 'tally', title: readTitle(record.title), rows };
  }
  if (record.type === 'dot_plot') return readDotPlot(record);
  if (record.type === 'pie_chart') return readPieChart(record);
  if (record.type === 'table') return readTable(record);
  if (record.type === 'coordinate_grid') return readCoordinateGrid(record);
  if (record.type === 'map_grid') return readMapGrid(record);
  if (record.type === 'number_line') return readNumberLine(record);
  if (record.type === 'fraction_model') return readFractionModel(record);
  if (record.type === 'shade_grid') return readShadeGrid(record);
  if (record.type === 'angle') return readAngle(record);
  if (record.type === 'angles_at_point') return readAnglesAtPoint(record);
  if (record.type === 'transversal') return readTransversal(record);
  if (record.type === 'shape_2d') return readShape2d(record);
  if (record.type === 'composite_shape') return readCompositeShape(record);
  if (record.type === 'solid_3d') return readSolid3d(record);
  if (record.type === 'net') return readNet(record);
  if (record.type === 'net_grid') return readNetGrid(record);
  if (record.type === 'circle_parts') return readCircleParts(record);
  if (record.type === 'cube_stack') return readCubeStack(record);
  if (record.type === 'curved_shape') return readCurvedShape(record);
  if (record.type === 'shape_pair') return readShapePair(record);
  if (record.type === 'quad_diagonals') return readQuadDiagonals(record);
  if (record.type === 'tree_diagram') return readTreeDiagram(record);
  if (record.type === 'venn') return readVenn(record);
  if (record.type === 'axes_3d') return readAxes3d(record);
  if (record.type === 'thermometer') return readThermometer(record);
  if (record.type === 'digital_clock') return readDigitalClock(record);
  if (record.type === 'area_model') return readAreaModel(record);
  if (record.type === 'clock') return readClock(record);
  if (record.type === 'base_ten') return readBaseTen(record);
  if (record.type === 'counters') return readCounters(record);
  if (record.type === 'array') return readArray(record);
  if (record.type === 'money') return readMoney(record);
  if (record.type === 'ruler') return readRuler(record);
  if (record.type === 'jug') return readJug(record);
  if (record.type === 'scale') return readScale(record);
  if (record.type === 'bar_model') return readBarModel(record);
  if (record.type === 'picture_pattern') return readPicturePattern(record);
  if (record.type === 'cups') return readCups(record);
  if (record.type === 'unit_line') return readUnitLine(record);
  if (record.type === 'balance') return readBalance(record);
  if (record.type === 'turn_picture') return readTurnPicture(record);
  if (record.type === 'ten_frame') return readTenFrame(record);
  if (record.type === 'hundred_chart') return readHundredChart(record);
  if (record.type === 'line_up') return readLineUp(record);
  if (record.type === 'everyday_object') return readEverydayObject(record);
  return null;
}
